import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import Layout from 'antd/lib/layout/index';
import Menu from 'antd/lib/menu/index';
import Avatar from 'antd/lib/avatar';
import Affix from 'antd/lib/affix';
import Drawer from 'antd/lib/drawer';
import Dropdown from 'antd/lib/dropdown';
import Grid from 'antd/lib/grid';
import Segmented from 'antd/lib/segmented';
import theme from 'antd/lib/theme';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  ToolOutlined,
  SearchOutlined,
  EditOutlined,
  LogoutOutlined,
  AuditOutlined,
  UserOutlined,
  ContainerOutlined,
  FileMarkdownOutlined,
  KeyOutlined,
  UpCircleFilled,
  DownCircleFilled,
  HourglassOutlined,
  FilterOutlined,
  PieChartOutlined,
  SettingOutlined,
  SolutionOutlined,
  RocketOutlined,
  FileProtectOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import LogoImage from './assets/one-air-logo-07.png';
import styles from './BaseLayout.module.css';
import { DEFAULT_PATHS } from '../utils/ApiUtils';
import { scrollTop } from '../utils/MiscUtils';
import PermissionsChecker, { CATEGORIES } from '../utils/PermissionsChecker';
import { BUSINESS_DATA_TABS } from '../utils/BusinessDataTabs';
import { COMMERCIAL_CONTROLS_TABS } from '../utils/CommercialControlsTabs';
import { ANCILLARIES_TABS } from '../utils/AncillariesTabs';

const { Header, Sider, Content } = Layout;

const pathsAndKeys = {
  sm: '1',
  bcm: '3',
  bcm_diagn: '4',
  commercial_controls: '10',
  provider_preference: '15',
  mu: '16',
  bcm_remarks: '17',
  ticket_tlo: '18',
  provider_credentials: '19',
  gf_booking_classes: '20',
  gf_double_connections: '21',
  gf_multi_airports: '22',
  gf_marketing_and_operating_carriers: '23',
  gf_fare_types: '24',
  gf_fare_basis: '25',
  gf_ticket_designators: '26',
  gf_flight_numbers: '27',
  gf_bagless_fares: '28',
  shopping_diagnostics: '30',
  user_permission: '31',
  identity_provider_settings: '32',
  identity_settings: '33',
  gf: '34',
  activity_log: '35',
  providers: '40',
  vcc_credentials: '41',
  sm_new: '42',
  ssr_credentials: '43',
  booking_tolerances: '44',
  ancillaries: '45',
  vcc_credentials_conferma: '48',
  vcc_credentials_wex: '49',
  master_data_setup: '50',
  business_data: '51',
};

type GetKeyByPath = (pathname: string | undefined) => string;

const getKeyByPath: GetKeyByPath = (pathname) => {
  if (pathname) {
    if (pathname.split('/')[1] === 'ancillaries') {
      return pathsAndKeys.ancillaries;
    } else if (pathname.includes(DEFAULT_PATHS.SEARCHES_MANAGER)) {
      return pathsAndKeys.sm;
    } else if (pathname.includes(DEFAULT_PATHS.PROVIDER_PREFERENCE)) {
      return pathsAndKeys.provider_preference;
    } else if (pathname.includes(DEFAULT_PATHS.PROVIDER_CREDENTIALS)) {
      return pathsAndKeys.provider_credentials;
    } else if (pathname.includes(DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS)) {
      return pathsAndKeys.identity_provider_settings;
    } else if (pathname.includes(DEFAULT_PATHS.IDENTITY_SETTINGS)) {
      return pathsAndKeys.identity_settings;
    } else if (pathname.includes(DEFAULT_PATHS.MASTER_DATA_SETUP)) {
      return pathsAndKeys.master_data_setup;
    } else if (pathname.split('/')[1] === DEFAULT_PATHS.DIAGNOSTICS) {
      return pathsAndKeys.shopping_diagnostics;
    } else if (pathname.includes(DEFAULT_PATHS.SHOPPING_DIAGNOSTICS_V2)) {
      return pathsAndKeys.shopping_diagnostics;
    } else if (pathname.includes(DEFAULT_PATHS.SHOPPING_DIAGNOSTICS)) {
      return pathsAndKeys.shopping_diagnostics;
    } else if (pathname.includes(DEFAULT_PATHS.TICKET_TLO)) {
      return pathsAndKeys.ticket_tlo;
    } else if (pathname.includes(DEFAULT_PATHS.BOOKING_TOLERANCES)) {
      return pathsAndKeys.booking_tolerances;
    } else if (pathname.includes(DEFAULT_PATHS.BOOKING_CREATE_MANAGER)) {
      if (pathname.includes(DEFAULT_PATHS.REMARKS)) {
        return pathsAndKeys.bcm_remarks;
      } else if (pathname.includes(DEFAULT_PATHS.SSR)) {
        return pathsAndKeys.ssr_credentials;
      }
      return pathsAndKeys.bcm;
    } else if (pathname.includes(DEFAULT_PATHS.BUSINESS_DATA)) {
      if (pathname.includes(DEFAULT_PATHS.COMMERCIAL_CONTROLS)) {
        return pathsAndKeys.commercial_controls;
      }
      return pathsAndKeys.business_data;
    } else if (pathname.includes(DEFAULT_PATHS.MARKUPS)) {
      return pathsAndKeys.mu;
    } else if (pathname.includes(DEFAULT_PATHS.USER_PERMISSIONS)) {
      return pathsAndKeys.user_permission;
    } else if (pathname.includes(DEFAULT_PATHS.GLOBAL_FILTERS)) {
      return pathsAndKeys.gf;
    } else if (pathname.includes(DEFAULT_PATHS.VCC_CREDENTIALS)) {
      return pathsAndKeys.vcc_credentials;
    } else if (pathname.includes(DEFAULT_PATHS.ACTIVITY_LOG)) {
      return pathsAndKeys.activity_log;
    } else if (pathname.includes(DEFAULT_PATHS.TECHNICAL_LOGS)) {
      return pathsAndKeys.shopping_diagnostics;
    } else if (pathname.includes(DEFAULT_PATHS.ANCILLARY_MANAGER)) {
      return pathsAndKeys.ancillaries;
    } else if (pathname.includes(DEFAULT_PATHS.ANCILLARY_SUPPLIER)) {
      return pathsAndKeys.ancillaries;
    } else if (pathname.includes(DEFAULT_PATHS.VCC_CREDENTIALS_CONFERMA)) {
      return pathsAndKeys.vcc_credentials_conferma;
    } else if (pathname.includes(DEFAULT_PATHS.ANCILLARY_DISTRIBUTION)) {
      return pathsAndKeys.ancillaries;
    } else if (pathname.includes(DEFAULT_PATHS.VCC_CREDENTIALS_WEX)) {
      return pathsAndKeys.vcc_credentials_wex;
    }
    return '0';
  }
  return '0';
};

export const DROP_OPTION_LOGOUT = {
  key: 'logout',
  name: `Log Out`,
  icon: <LogoutOutlined />,
};

const sectionTitles: Record<string, string> = {
  ancillaries: 'Ancillaries',
  [DEFAULT_PATHS.SEARCHES_MANAGER]: 'Searches Manager',
  [DEFAULT_PATHS.SEARCHES_MANAGER_V2]: 'Searches Manager',
  [DEFAULT_PATHS.BOOKING_CREATE_MANAGER]: 'PNR Create Manager',
  [DEFAULT_PATHS.BOOKING_CREATE_MANAGER_V2]: 'PNR Create Manager',
  [DEFAULT_PATHS.BUSINESS_DATA]: 'Business Data',
  [DEFAULT_PATHS.BOOKING_TOLERANCES]: 'Booking Tolerances',
  [DEFAULT_PATHS.USER_PERMISSIONS]: 'User Permissions',
  [DEFAULT_PATHS.PROVIDER_CREDENTIALS]: 'Supplier Credentials',
  [DEFAULT_PATHS.PROVIDER_PREFERENCE]: 'Supplier Preference',
  [DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS]: 'Identity Supplier Settings',
  [DEFAULT_PATHS.IDENTITY_SETTINGS]: 'Identity Settings',
  [DEFAULT_PATHS.MASTER_DATA_SETUP]: 'Master Data Setup',
  [DEFAULT_PATHS.MARKUPS]: 'Markups',
  [DEFAULT_PATHS.MARKUPS_V2]: 'Markups',
  [DEFAULT_PATHS.GLOBAL_FILTERS]: 'Global Filters',
  [DEFAULT_PATHS.GLOBAL_FILTERS_V2]: 'Global Filters',
  [DEFAULT_PATHS.VCC_CREDENTIALS]: 'VCC Credentials',
  [DEFAULT_PATHS.VCC_CREDENTIALS_CONFERMA]: 'VCC Credentials',
  [DEFAULT_PATHS.VCC_CREDENTIALS_WEX]: 'VCC Credentials',
  [DEFAULT_PATHS.TICKET_TLO]: 'Ticket TLO',
  [DEFAULT_PATHS.TICKET_TLO_V2]: 'Ticket TLO',
  [DEFAULT_PATHS.DIAGNOSTICS]: 'Diagnostics',
  [DEFAULT_PATHS.SHOPPING_DIAGNOSTICS]: 'Diagnostics',
  [DEFAULT_PATHS.SHOPPING_DIAGNOSTICS_V2]: 'Diagnostics',
  [DEFAULT_PATHS.MARKUP_DIAGNOSTICS]: 'Markup Diagnostics',
  [DEFAULT_PATHS.GLOBAL_FILTERS_DIAGNOSTICS]: 'Global Filters Diagnostics',
  [DEFAULT_PATHS.ACTIVITY_LOG]: 'Activity Log',
  [DEFAULT_PATHS.TECHNICAL_LOGS]: 'Diagnostics',
  [DEFAULT_PATHS.ANCILLARY_MANAGER]: 'Ancillaries',
  [DEFAULT_PATHS.ANCILLARY_SUPPLIER]: 'Ancillaries',
  [DEFAULT_PATHS.ANCILLARY_DISTRIBUTION]: 'Ancillaries',
  [DEFAULT_PATHS.PROVIDERS]: 'Supplier',
};

const getOpenKeysByPath = (pathname: string): string[] => {
  return [];
};

interface IBaseLayoutProps {
  children: any;
}

const businessDataSectionTitles: Record<string, string> = {
  [DEFAULT_PATHS.COMMERCIAL_CONTROLS]: 'Commercial Controls',
  [DEFAULT_PATHS.MARKETS]: 'Commercial Controls',
  [DEFAULT_PATHS.DISTRIBUTION_CHANNELS]: 'Commercial Controls',
};

const BaseLayout: React.FC<IBaseLayoutProps> = ({ children }) => {
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterAppearance, setFilterAppearance] = useState<'light' | 'dark'>('light');
  const [openKeys, setOpenKeys] = useState<string[]>(() => getOpenKeysByPath(location.pathname));
  const [showFastScrollButtons, setShowFastScrollButtons] = useState(false);
  const selectedKey = getKeyByPath(location.pathname);
  const sectionTitle = location.pathname.split('/')[1] === DEFAULT_PATHS.BUSINESS_DATA
    ? businessDataSectionTitles[location.pathname.split('/')[2]] || 'Business Data'
    : sectionTitles[location.pathname.split('/')[1]];
  const hideFastScrollButtons =
    location.pathname.split('/')[1] === DEFAULT_PATHS.DIAGNOSTICS ||
    location.pathname.includes(DEFAULT_PATHS.SHOPPING_DIAGNOSTICS_V2) || location.pathname.includes(DEFAULT_PATHS.SHOPPING_DIAGNOSTICS);

  const authContext: any = React.useContext(AuthContext);
  const userPermissions = authContext.userPermissions;

  useEffect(() => {
    setShowFastScrollButtons(false);
    setOpenKeys(getOpenKeysByPath(location.pathname));
    setMobileMenuOpen(false);
    scrollTop();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowFastScrollButtons(Boolean(window.scrollY || document.body.scrollHeight > document.body.clientHeight));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenKeys(!isMobile && collapsed ? [] : getOpenKeysByPath(location.pathname));
  }, [isMobile]);

  const toggleCollapse = useCallback(
    (): void => {
      if (isMobile) {
        setOpenKeys(getOpenKeysByPath(location.pathname));
        setMobileMenuOpen(!mobileMenuOpen);
      } else {
        setCollapsed(!collapsed);
        setOpenKeys(collapsed ? getOpenKeysByPath(location.pathname) : []);
      }
    },
    [collapsed, isMobile, mobileMenuOpen, location.pathname]
  );

  const handleMenuClick = useCallback(
    (param: any) => {
      switch (param.key) {
        case DROP_OPTION_LOGOUT.key:
          authContext?.onLogOut();
          return;
        default:
          return;
      }
    },
    [authContext]
  );

  const sidebar = (
    <nav className={styles.navigation} id={isMobile ? 'mobile-navigation' : 'desktop-navigation'} aria-label="Primary navigation">
          {PermissionsChecker.isPermitted(CATEGORIES.ADMIN_PORTAL, userPermissions) ? (
            <Menu
              mode="inline"
              theme="dark"
              className={styles.siderMenu}
              style={{ fontFamily: theme.defaultSeed.fontFamily }}
              selectedKeys={[selectedKey]}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Menu.ItemGroup key="workspace" title="Workspace">
              {PermissionsChecker.isPermitted(CATEGORIES.SEARCHES_MANAGER, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.sm} icon={<PieChartOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.SEARCHES_MANAGER}`}>
                    Searches Manager
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS, userPermissions) ||
                PermissionsChecker.isPermitted(CATEGORIES.BOOKING_CREATE_MANAGER_OSIS, userPermissions) || 
                PermissionsChecker.isPermitted(CATEGORIES.BOOKING_CREATE_MANAGER_SSR, userPermissions)      ? (
                    <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.bcm} icon={<AuditOutlined />}>
                      <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}`}>
                       PNR Create Manager
                      </Link>
                    </Menu.Item>
                  ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.MARKUPS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.mu} icon={<FileMarkdownOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.MARKUPS}`}>
                    Markups
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.GLOBAL_FILTERS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.gf} icon={<FilterOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.GLOBAL_FILTERS}/`}>
                    Global Filters
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.TICKET_TLO, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.ticket_tlo} icon={<HourglassOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.TICKET_TLO}`}>
                    Ticket TLO
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.BOOKING_TOLERANCES, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.booking_tolerances} icon={<EditOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}`}>
                    Booking Tolerances
                  </Link>
                </Menu.Item>
              ) : null}
              {ANCILLARIES_TABS.some((tab) => PermissionsChecker.isPermitted(tab.category, userPermissions)) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.ancillaries} icon={<FileProtectOutlined />}>
                  <Link className={styles.siderLink} to="/ancillaries">Ancillaries</Link>
                </Menu.Item>
              ) : null}
              </Menu.ItemGroup>
              {[
                CATEGORIES.USER_PERMISSIONS,
                ...BUSINESS_DATA_TABS.map((tab) => tab.category),
                ...COMMERCIAL_CONTROLS_TABS.map((tab) => tab.category),
                CATEGORIES.PROVIDER_CREDENTIALS,
                CATEGORIES.IDENTITY_PROVIDER_SETTINGS,
                CATEGORIES.VCC_CREDENTIALS,
                CATEGORIES.PROVIDERS,
              ].some((category) => PermissionsChecker.isPermitted(category, userPermissions)) ? (
              <Menu.ItemGroup key="configuration" title="Configuration">
              {PermissionsChecker.hasWriteAccess(CATEGORIES.USER_PERMISSIONS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.user_permission} icon={<UserOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.USER_PERMISSIONS}`}>
                    User Permissions
                  </Link>
                </Menu.Item>
              ) : null}
              {BUSINESS_DATA_TABS.some((tab) => PermissionsChecker.isPermitted(tab.category, userPermissions)) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.business_data} icon={<ContainerOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.BUSINESS_DATA}`}>
                    Business Data
                  </Link>
                </Menu.Item>
              ) : null}
              {COMMERCIAL_CONTROLS_TABS.some((tab) => PermissionsChecker.isPermitted(tab.category, userPermissions)) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.commercial_controls} icon={<ContainerOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}`}>
                    Commercial Controls
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.PROVIDER_CREDENTIALS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.provider_credentials} icon={<KeyOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}`}>
                    Supplier Credentials
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.IDENTITY_PROVIDER_SETTINGS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.identity_provider_settings} icon={<SettingOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}`}>
                    Identity Supplier Settings
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.IDENTITY_PROVIDER_SETTINGS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.master_data_setup} icon={<SettingOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.MASTER_DATA_SETUP}`}>
                    Master Data Setup
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.VCC_CREDENTIALS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.vcc_credentials} icon={<CreditCardOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.VCC_CREDENTIALS}`}>
                    VCC Credentials
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.PROVIDERS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.providers} icon={<RocketOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.PROVIDERS}`}>
                    Supplier
                  </Link>
                </Menu.Item>
              ) : null}
              </Menu.ItemGroup>
              ) : null}
              <Menu.ItemGroup key="monitoring" title="Monitoring">
              {PermissionsChecker.isPermitted(CATEGORIES.SHOPPING_DIAGNOSTICS, userPermissions) ||
                PermissionsChecker.isPermitted(CATEGORIES.TECHNICAL_LOGS, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.shopping_diagnostics} icon={<ToolOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.DIAGNOSTICS}`}>
                    Diagnostics
                  </Link>
                </Menu.Item>
              ) : null}
              {PermissionsChecker.isPermitted(CATEGORIES.ACTIVITY_LOG, userPermissions) ? (
                <Menu.Item className={styles.siderMenuItem} key={pathsAndKeys.activity_log} icon={<SolutionOutlined />}>
                  <Link className={styles.siderLink} to={`/${DEFAULT_PATHS.ACTIVITY_LOG}`}>
                    Activity Log
                  </Link>
                </Menu.Item>
              ) : null}
              </Menu.ItemGroup>
            </Menu>
          ) : null}
      <button
        type="button"
        className={styles.sidebarToggle}
        onClick={toggleCollapse}
        aria-label={isMobile ? 'Close navigation' : collapsed ? 'Expand navigation' : 'Collapse navigation'}
        title={isMobile ? 'Close navigation' : collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {!isMobile && collapsed ? <RightOutlined /> : <LeftOutlined />}
        {isMobile || !collapsed ? <span>{isMobile ? 'Close menu' : 'Collapse menu'}</span> : null}
      </button>
    </nav>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header className={styles.header} style={{ fontFamily: theme.defaultSeed.fontFamily }}>
        <div className={`${styles.brand} ${!isMobile && collapsed ? styles.collapsed : ''}`}>
          <img src={LogoImage} width={80} height={40} alt="One Air Logo" />
        </div>
        <div className={styles.headerContext}>
          <button
            type="button"
            className={styles.toggleCollapseButton}
            onClick={toggleCollapse}
            aria-label={isMobile ? 'Open navigation' : collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={isMobile ? 'Open navigation' : collapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={isMobile ? mobileMenuOpen : !collapsed}
            aria-controls={isMobile ? 'mobile-navigation' : 'desktop-navigation'}
          >
            {isMobile || collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
          <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
            <span className={styles.portalLabel}>Admin Portal</span>
            {sectionTitle ? (
              <>
                <RightOutlined className={styles.breadcrumbSeparator} />
                <span className={styles.sectionTitle} aria-current="page">{sectionTitle}</span>
              </>
            ) : null}
          </nav>
        </div>
        <div className={styles.appearanceControl} title="Search panel appearance">
          <Segmented
            size="small"
            aria-label="Search panel appearance"
            value={filterAppearance}
            options={[
              { label: 'Light', value: 'light' },
              { label: 'Dark', value: 'dark' },
            ]}
            onChange={(value) => setFilterAppearance(value as typeof filterAppearance)}
          />
        </div>
        {authContext.isLoggedIn ? (
          <Dropdown
            menu={{
              items: [{ key: DROP_OPTION_LOGOUT.key, label: DROP_OPTION_LOGOUT.name, icon: DROP_OPTION_LOGOUT.icon }],
              onClick: handleMenuClick,
            }}
            trigger={['click']}
          >
            <button type="button" className={styles.accountButton} aria-label="Account menu" title="Account menu">
              <Avatar className={styles.accountAvatar} icon={<UserOutlined />} />
              <span className={styles.accountLabel}>Account</span>
              <DownOutlined />
            </button>
          </Dropdown>
        ) : null}
      </Header>
      <Layout>
        {!isMobile ? (
          <Sider className={styles.sider} collapsible collapsed={collapsed} collapsedWidth={72} trigger={null} width={240}>
            {sidebar}
          </Sider>
        ) : null}
        <Drawer
          title={<img src={LogoImage} width={80} height={40} alt="One Air Logo" />}
          placement="left"
          width={240}
          open={isMobile && mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          rootClassName={styles.mobileDrawer}
          bodyStyle={{ padding: 0 }}
        >
          {isMobile ? sidebar : null}
        </Drawer>
        <Layout>
          {showFastScrollButtons && !hideFastScrollButtons && (
            <>
              <Affix style={{ position: 'absolute', top: 58, right: 13, zIndex: 99 }}>
                <UpCircleFilled
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                  style={{
                    marginTop: '20px',
                    fontSize: '24px',
                    color: 'rgb(24, 144, 255)',
                  }}
                />
              </Affix>
              <Affix style={{ position: 'absolute', top: 58, right: 43, zIndex: 99 }}>
                <DownCircleFilled
                  onClick={() =>
                    window.scrollTo({
                      top: document.body.scrollHeight,
                      left: 0,
                      behavior: 'smooth',
                    })
                  }
                  style={{
                    marginTop: '20px',
                    fontSize: '24px',
                    color: 'rgb(24, 144, 255)',
                  }}
                />
              </Affix>
            </>
          )}
          <Content className={styles.content} data-filter-appearance={filterAppearance}>{children}</Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default BaseLayout;
